import './ShareProjectUserList.scss';
import { Uuid } from '@belimo-retrofit-portal/logic';
import { constNull } from 'fp-ts/lib/function';
import React, { useMemo, useState } from 'react';
import { FormattedMessage } from 'src/migration/react-intl';
import { Avatar } from 'src/modules/common/components/Avatar';
import { Button } from 'src/modules/common/components/Button';
import { HorizontalDivider } from 'src/modules/common/components/HorizontalDivider';
import { OverflowMenu } from 'src/modules/common/components/OverflowMenu';
import { INTL_LOCALE_MAP } from 'src/modules/common/constants/language';
import { ChevronDownIcon } from 'src/modules/common/icons/ChevronDownIcon';
import { ChevronUpIcon } from 'src/modules/common/icons/ChevronUpIcon';
import { DeleteIcon } from 'src/modules/common/icons/DeleteIcon';
import { AuthUser } from 'src/modules/common/types/AuthUser';
import { Project } from 'src/modules/common/types/Project';
import { ProjectRole } from 'src/modules/common/types/ProjectRole';
import { BodyMedium01 } from 'src/modules/common/typography/BodyMedium01';
import { BodyMedium02 } from 'src/modules/common/typography/BodyMedium02';
import { HelperText01 } from 'src/modules/common/typography/HelperText01';
import { SharedUser } from 'src/modules/sharing-projects/types/SharedUser';
import { Language } from 'src/types/common/Language';

type Props = {
  readonly language: Language;
  readonly project: Project;
  readonly loggedInUser: AuthUser;
  readonly users: ReadonlyArray<SharedUser>;
  readonly onRemove: (user: SharedUser) => void;
  readonly changeUserRole: (args: {
    userId: Uuid;
    role: ProjectRole;
  }) => void;
  readonly changeOwnRole: (args: {
    user: SharedUser;
    role: ProjectRole;
  }) => void;
  readonly changeProjectOwner: (user: SharedUser) => void;
};

export const ShareProjectUserList = ({
  language,
  project,
  loggedInUser,
  users,
  onRemove,
  changeUserRole,
  changeOwnRole,
  changeProjectOwner,
}: Props): React.ReactElement => {
  const [openedMenu, setOpenedMenu] = useState<Uuid | null>(null);

  const currentUser = users.find((user) => user.id === loggedInUser.id);
  const currentUserRole = currentUser?.role;

  const sortedUsers = useMemo(() => {
    const collator = new Intl.Collator(INTL_LOCALE_MAP[language]);

    return [...users].sort((a, b) => collator.compare(`${a.firstName} ${a.lastName}`, `${b.firstName} ${b.lastName}`));
  }, [language, users]);

  const handleChangeRole = (user: SharedUser, role: ProjectRole):void => {
    if (user.id === loggedInUser.id && role === ProjectRole.VIEWER) {
      changeOwnRole({ user, role });
    } else {
      changeUserRole({ userId: user.id, role: role });
    }
    setOpenedMenu(null);
  };

  const handleChangeOwner = (user: SharedUser): void => {
    changeProjectOwner(user);
    setOpenedMenu(null);
  };

  return (
    <div className="bp-share-project-user-list">
      <p className="bp-share-project-user-list__title">
        <FormattedMessage id="shareProject/modal/title"/>
      </p>

      <div className="bp-share-project-user-list__list">
        {project.owner !== null && (
          <>
            <div className="bp-share-project-user-list__cell">
              <Avatar user={project.owner}/>
            </div>

            <div className="bp-share-project-user-list__cell">
              <BodyMedium01 className="bp-share-project-user-list__name">
                {project.owner.firstName} {project.owner.lastName}
              </BodyMedium01>

              <HelperText01 className="bp-share-project-user-list__email">
                {project.owner.email}
              </HelperText01>
            </div>

            <div className="bp-share-project-user-list__cell">
              <Button kind="ghost" disabled={true}>
                <BodyMedium02>
                  <FormattedMessage id="shareProject/modal/owner"/>
                </BodyMedium02>
              </Button>
            </div>
          </>
        )}

        {sortedUsers.map((user) => (
          <React.Fragment key={user.id}>
            <div className="bp-share-project-user-list__cell">
              <Avatar user={user}/>
            </div>

            <div className="bp-share-project-user-list__cell">
              <BodyMedium01 className="bp-share-project-user-list__name">
                {user.firstName} {user.lastName}
              </BodyMedium01>

              <HelperText01 className="bp-share-project-user-list__email">
                {user.email}
              </HelperText01>
            </div>

            <OverflowMenu
              id={`share-project-user-list-${user.id}-menu`}
              label={(
                <button
                  className="bp-share-project-user-list__dropdown-button"
                  type="button"
                  disabled={currentUserRole === ProjectRole.VIEWER}
                  onClick={() => (user.id === openedMenu ? setOpenedMenu(null) : setOpenedMenu(user.id))}
                >
                  <FormattedMessage id={`shareProject/modal/${user.role}`}/>

                  <div className="bp-share-project-user-list__dropdown-button-icon">
                    {user.id === openedMenu ? <ChevronUpIcon/> : <ChevronDownIcon/>}
                  </div>
                </button>
              )}
              buttonTooltip={null}
              opened={user.id === openedMenu}
              setOpened={user.id === openedMenu ? (): void => setOpenedMenu(null) : constNull}
            >
              <div className="bp-share-project-user-list__action-list">
                <Button
                  className="bp-share-project-user-list__button"
                  fullWidth={true}
                  kind="ghost"
                  onClick={() => handleChangeRole(user, ProjectRole.EDITOR)}
                >
                  <BodyMedium02>
                    <FormattedMessage id="shareProject/modal/editor"/>
                  </BodyMedium02>
                </Button>

                <Button
                  className="bp-share-project-user-list__button"
                  fullWidth={true}
                  kind="ghost"
                  onClick={() => handleChangeRole(user, ProjectRole.VIEWER)}
                >
                  <BodyMedium02>
                    <FormattedMessage id="shareProject/modal/viewer"/>
                  </BodyMedium02>
                </Button>

                {loggedInUser.id === project.owner?.id && (
                  <Button
                    className="bp-share-project-user-list__button"
                    fullWidth={true}
                    kind="ghost"
                    onClick={() => handleChangeOwner(user)}
                  >
                    <BodyMedium02>
                      <FormattedMessage id="shareProject/modal/owner"/>
                    </BodyMedium02>
                  </Button>
                )}

                <HorizontalDivider className="bp-share-project-user-list__action-divider"/>

                {loggedInUser.id !== user.id && (
                  <Button
                    className="bp-share-project-user-list__button bp-share-project-user-list__button--danger"
                    fullWidth={true}
                    kind="danger"
                    onClick={() => onRemove(user)}
                  >
                    <BodyMedium02>
                      <FormattedMessage id="shareProject/modal/action/removeUser"/>
                    </BodyMedium02>

                    <div className="bp-share-project-user-list__button-icon">
                      <DeleteIcon/>
                    </div>
                  </Button>
                )}
              </div>
            </OverflowMenu>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};
